import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Dialog, Grid, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { useTranslation } from "react-i18next";
import ControlledPhoneInput from "../../../../../../components/controlled/ControlledPhoneInput";
import ControlledTextField from "../../../../../../components/controlled/ControlledTextField";
import TitledBox from "../../../../../../components/TitledBox";
import { useEditRecipientMutation } from "../../../../recipientsApiSlice";
import { getFormSchema } from "./formSchema";

export const EditDialog = ({
	onClose,
	open,
	groupId,
	recipientId,
	recipient,
}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [editRecipientMutation, { isLoading }] = useEditRecipientMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
	} = useForm({
		defaultValues: recipient,
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const handleEditRecipient = async (data) => {
		const { name, firstName, lastName, email, phone, nip } = data;
		await editRecipientMutation({
			groupId,
			recipientId,
			data: {
				name,
				firstName,
				lastName,
				email,
				phone,
				nip,
			},
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("updated_carrier_data"), {
					variant: "success",
				});
				onClose();
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	return (
		<Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
			<Box sx={{ padding: "30px" }}>
				<Box
					component="form"
					noValidate
					onSubmit={handleSubmit(handleEditRecipient)}
				>
					<Grid container rowSpacing={3.6} columnSpacing={2}>
						<Grid
							size={{
								lg: 12,
							}}
						>
							<TitledBox
								title={t("carrier_data")}
								color="#0069b4"
								sx={{ width: "100%", height: "100%" }}
							>
								<Grid
									container
									rowSpacing={0}
									columnSpacing={1}
									sx={{ py: 1, px: 2 }}
								>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledTextField
											margin="normal"
											label={t("name_label")}
											control={control}
											name="name"
											required
											error={!!errors?.name}
											fullWidth
										/>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<ControlledTextField
											margin="normal"
											label={t("first_name_label")}
											control={control}
											name="firstName"
											error={!!errors?.firstName}
											fullWidth
										/>
									</Grid>
									<Grid
										size={{
											lg: 6,
										}}
									>
										<ControlledTextField
											margin="normal"
											label={t("surname_label")}
											control={control}
											name="lastName"
											error={!!errors?.lastName}
											fullWidth
										/>
									</Grid>
								</Grid>
							</TitledBox>
						</Grid>
						<Grid
							size={{
								lg: 6,
							}}
						>
							<TitledBox
								title={t("contact")}
								color="#63c29a"
								sx={{ width: "100%", height: "100%" }}
							>
								<Grid
									container
									rowSpacing={0}
									columnSpacing={1}
									sx={{ py: 1, px: 2 }}
								>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledTextField
											control={control}
											label={t("email_label")}
											margin="normal"
											name="email"
											type="email"
											required
											error={!!errors?.email}
											fullWidth
										/>
									</Grid>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledPhoneInput
											control={control}
											label={t("phone_label")}
											margin="normal"
											name="phone"
											type="tel"
											error={!!errors?.phone}
											fullWidth
										/>
									</Grid>
								</Grid>
							</TitledBox>
						</Grid>
						<Grid
							size={{
								lg: 6,
							}}
						>
							<TitledBox
								title={t("nip_number_label")}
								color="#7f7f7f"
								sx={{ width: "100%", height: "100%" }}
							>
								<Grid
									container
									rowSpacing={0}
									columnSpacing={1}
									sx={{ py: 1, px: 2 }}
								>
									<Grid
										size={{
											lg: 12,
										}}
									>
										<ControlledTextField
											margin="normal"
											control={control}
											name="nip"
											error={!!errors?.nip}
											fullWidth
											sx={{ mb: 0 }}
										/>
									</Grid>
									<Grid size={12}>
										<Typography
											component="span"
											sx={{ color: "#A6A6A6", fontSize: "11px" }}
										>
											{t("nip_number_info")}
										</Typography>
									</Grid>
								</Grid>
							</TitledBox>
						</Grid>
						<Grid size={12}>
							<Box sx={{ display: "flex", justifyContent: "center" }}>
								<Button loading={isLoading} type="submit" variant="contained">
									{t("save")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Dialog>
	);
};
